/**
 * @lodex-pro/web-shared/auth/api-client — authed fetch wrapper for the lodeX Pro API.
 *
 * Phase 4 / Plan 04-09 — every web app call to the API goes through `authedFetch`:
 *
 *   - Reads the cached access token via `fetchAuthSession()` (Amplify keeps it in the
 *     in-memory map of `CookieExchangeStorageAdapter`).
 *   - Sets `Authorization: Bearer <accessToken>` + `credentials: 'include'` so cookies
 *     (lodex_did + lodex_refresh) auto-attach on every request.
 *   - On 401 from the API: POST `/v1/auth/refresh` (cookie-only — server reads
 *     `lodex_refresh` via httpOnly cookie). On a 2xx response, READ THE JSON BODY
 *     for the new access token, prime Amplify's in-memory adapter, then retry the
 *     original request ONCE with the new access token attached as Bearer DIRECTLY
 *     (no second `fetchAuthSession` round-trip on the retry hop — that was the bug).
 *     No second retry — a second 401 is surfaced to the caller.
 *
 * Phase 4 / Plan 04-11 (CR-06 fix): the original retry path called
 * `fetchAuthSession({ forceRefresh: true })` which made Amplify try to re-read the
 * refresh token from storage; our adapter returns null for `.refreshToken` keys
 * (the cookie is HttpOnly) so the forced refresh failed, the catch swallowed it,
 * and the retry attached the SAME expired token that caused the original 401.
 * The new flow:
 *   1. POST /v1/auth/refresh — server reads lodex_refresh cookie, returns
 *      { accessToken, expiresIn }.
 *   2. Decode the access token's `sub` claim + write into Amplify's adapter via
 *      `primeAccessTokenFromRefresh` (best-effort; on failure the retry still
 *      succeeds because the Bearer is attached directly from the refresh body).
 *   3. Retry with `Authorization: Bearer <new accessToken>` attached directly
 *      from the refresh response body.
 *
 * Spec: master spec §7 (web auth); §31 (three-audience error model); §43 (cookie
 *       hardening checklist); CONTEXT D-07; RESEARCH § Pattern 5 + § Sequence
 *       Diagram E lines 1175-1217; Plan 04-11 — CR-06 fix.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

import { primeAccessTokenFromRefresh } from './amplify-bootstrap.js';
import { refreshViaCookie } from './refresh-gate.js';

/**
 * Configuration for `authedFetch`. `baseUrl` is the same value the storage adapter
 * was constructed with (same-origin requirement for the SameSite=Strict cookie).
 */
export interface WebApiOpts {
  /** Full origin of the lodeX Pro API, no trailing slash. */
  readonly baseUrl: string;
}

/**
 * Read the cached access token via Amplify. Returns `null` if the session has no token
 * (signed-out or not-yet-bootstrapped state). We never throw — the caller decides what
 * to do with a missing token (typically: route the user to sign-in).
 */
/**
 * Operator portal rework PR 5 — a bearer token that replaces the Amplify session for every
 * request of this tab: the "view as operator" session token (`vas_…`) the portal got by
 * exchanging a one-time code. While it is set, a 401 is final (the session ended or expired):
 * no cookie refresh, which would otherwise swap in whoever is signed in on this browser.
 */
let accessTokenOverride: string | null = null;
let onOverrideUnauthorized: (() => void) | null = null;

/**
 * Set (or clear, with null) the bearer token override. See `accessTokenOverride`.
 * `onUnauthorized` runs when a request made with it comes back 401 (the session is over).
 */
export function setAccessTokenOverride(
  token: string | null,
  opts: { onUnauthorized?: () => void } = {},
): void {
  accessTokenOverride = token;
  onOverrideUnauthorized = token === null ? null : (opts.onUnauthorized ?? null);
}

/** The bearer token override in force, or null. */
export function getAccessTokenOverride(): string | null {
  return accessTokenOverride;
}

async function getAccessToken(): Promise<string | null> {
  if (accessTokenOverride !== null) return accessTokenOverride;
  try {
    const session = await fetchAuthSession();
    const tok = session.tokens?.accessToken;
    if (tok === undefined) return null;
    return tok.toString();
  } catch {
    return null;
  }
}

/**
 * Build the headers for an authed request. `Content-Type: application/json` is the
 * lodeX Pro API default; callers passing a non-JSON body (e.g. multipart upload) should
 * pass their own `Content-Type` via `init.headers` and we will override.
 *
 * The Authorization header is omitted entirely (not set to an empty string or
 * "Bearer undefined") if no access token is available — `exactOptionalPropertyTypes`
 * makes the conditional-spread idiom the canonical way to do this.
 */
// Exported for unit testing (api-client.test.ts) — the case-insensitive merge below
// is the fix for the ERR-6DHW doubled-Content-Type bug and must be regression-covered.
export function buildHeaders(
  init: RequestInit | undefined,
  accessToken: string | null,
): HeadersInit {
  // Build on a `Headers` instance so the merge is CASE-INSENSITIVE. A plain-object
  // spread is case-SENSITIVE: a caller passing `content-type` (lowercase) alongside
  // our default `Content-Type` leaves BOTH keys in the object, and fetch() then
  // concatenates them on the wire into `application/json, application/json`, which the
  // API rejects as FST_ERR_CTP_INVALID_MEDIA_TYPE (surfaced as a 500 — see ERR-6DHW,
  // 2026-07-25). `Headers.set` replaces by lowercased name, so a caller's Content-Type
  // in any casing overrides the default exactly once.
  const headers = new Headers();
  // For a FormData body, DON'T set Content-Type — the browser must set it to
  // `multipart/form-data; boundary=…` itself (Task 10.6 logo upload). Otherwise
  // default to JSON.
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  if (!isFormData) {
    headers.set('Content-Type', 'application/json');
  }
  // Authorization is omitted entirely (not "Bearer undefined") when no token is available.
  if (accessToken !== null) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  // Caller-provided headers win — `.set()` replaces case-insensitively. `new Headers`
  // normalizes every HeadersInit variant (Headers instance, [name,value][] array, or
  // Record), so the previous per-variant branching is no longer needed.
  const callerHeaders = init?.headers;
  if (callerHeaders !== undefined) {
    new Headers(callerHeaders).forEach((value, name) => {
      headers.set(name, value);
    });
  }
  return headers;
}

/**
 * Obtain a fresh access token via the tab's shared refresh (refresh-gate.ts, OPEN-193).
 *
 * N components firing `authedFetch` on an expired token used to produce N simultaneous 401s,
 * each POSTing `/v1/auth/refresh` (~15–28 on a real page load). The gate keeps ONE request in
 * flight per tab, shared with useAuth's boot-time restore, and backs off after a 429 instead of
 * retrying. Resolves the new access token, or null when the refresh failed, is backing off or
 * has stopped: the caller then surfaces its original 401. Amplify is primed once per refresh.
 */
function refreshAccessToken(baseUrl: string): Promise<string | null> {
  // Passed at call time (not captured), so a test spy on the export sees the call.
  return refreshViaCookie(baseUrl, (token) => primeAccessTokenFromRefresh(token));
}

/**
 * fetch wrapper that handles auth headers + cookies + 401-to-refresh-and-retry.
 *
 * Retry policy: at most ONE refresh attempt per call. If the refresh POST itself
 * returns non-2xx, or the body is malformed, or the retried request still returns
 * 401, the caller receives the 401 Response and is responsible for routing the
 * user to sign-in.
 *
 * `credentials: 'include'` is the contract — the lodex_did cookie (SameSite=Lax,
 * Path=/) auto-attaches on every API call; the lodex_refresh cookie (SameSite=Strict,
 * Path=/v1/auth/refresh) auto-attaches only on the refresh endpoint.
 *
 * Plan 04-11 (CR-06 fix): refresh-body-read + primeAccessTokenFromRefresh +
 * direct-Bearer retry. No second fetchAuthSession round-trip on the retry hop —
 * that round-trip was the bug (Amplify cannot read the HttpOnly refresh cookie
 * so `forceRefresh: true` always failed silently).
 */
export async function authedFetch(
  opts: WebApiOpts,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const accessToken = await getAccessToken();
  const headers = buildHeaders(init, accessToken);

  let res = await fetch(`${opts.baseUrl}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (res.status !== 401) {
    return res;
  }
  // PR 5 — a view-as token is not refreshed; its 401 means the session is over.
  if (accessTokenOverride !== null) {
    onOverrideUnauthorized?.();
    return res;
  }

  // 401 path (Plan 04-11 CR-06 fix): obtain a fresh access token via a SHARED,
  // deduped refresh (see refreshAccessToken). Concurrent 401s await ONE
  // POST /v1/auth/refresh — server reads the HttpOnly refresh cookie and returns
  // { accessToken, expiresIn }; the old code went through fetchAuthSession, which
  // could not read that cookie and always re-attached the expired token.
  const newAccessToken = await refreshAccessToken(opts.baseUrl);
  if (newAccessToken === null) {
    // Refresh failed / returned an unusable body — surface the original 401.
    return res;
  }

  // Retry the original request ONCE with the new access token attached directly
  // (NOT going through fetchAuthSession again — that round-trip was the bug).
  const retryHeaders = buildHeaders(init, newAccessToken);
  res = await fetch(`${opts.baseUrl}${path}`, {
    ...init,
    headers: retryHeaders,
    credentials: 'include',
  });
  return res;
}
